import queryString, { type StringifiableRecord } from "query-string";
import speakingurl from "speakingurl";
import Cookies from "js-cookie";

export interface RouteAuthorization {
  name?: string;
  read?: boolean;
  write?: boolean;
}

interface CurrentUserCookie {
  adminRights?: RouteAuthorization[];
}

export interface RouteOptions {
  key: string;
  path?: string;
  parent?: RouteBase;
  hasOwnPage?: boolean;
  children?: Record<string, RouteBase> | null;
  icon?: string | null;
  priority: number;
  authorizationKey?: string;
  clickable?: boolean;
}

export interface RouteUrlParams {
  partial?: boolean;
  pathParams?: Record<string, string | number>;
  queryParams?: StringifiableRecord;
}

class RouteBase {
  key: string;

  path: string;

  fullPath: string;

  parent?: RouteBase;

  hasOwnPage: boolean;

  children: Record<string, RouteBase> | null;

  icon: string | null;

  priority: number;

  authorizationKey?: string;

  clickable: boolean;

  constructor({
    key,
    path,
    parent,
    hasOwnPage,
    children = null,
    icon = null,
    priority,
    authorizationKey,
    clickable = true,
  }: RouteOptions) {
    this.key = key;
    this.parent = parent;
    this.hasOwnPage = hasOwnPage ?? true;
    this.children = children;
    this.icon = icon;
    this.priority = priority; // Drawer order
    this.authorizationKey = authorizationKey; // For read and write authorization control in PageWrapper
    this.clickable = clickable;

    const isRootPath = path?.length === 1 && path === "/";

    this.path = `${isRootPath ? "" : "/"}${speakingurl(path || key, {
      uric: true, // allow special charachters: ";", "?", ":", "@", "&", "=", "+", "$", ",", "/"
    })}`;
    this.fullPath = "";

    this.toFullPath();
  }

  toFullPath(): string {
    let returnUrl = "";

    if (this.parent) {
      returnUrl = this.parent.toFullPath();
    }

    returnUrl += this.path;
    this.fullPath = returnUrl;

    return returnUrl;
  }

  /**
   * @desc Generates urls with urlSearchParams & query-strings
   * @example:
   * ===============
   * // For a route object with path products/:id/detail
   * routeObj.toUrl({ pathParams: { id: 1 } })
   *     //returns products/1/detail
   * routeObj.toUrl({ pathParams: { id: 1 }, queryParams: { page:1, sort: 'asc' } })
   *     // returns products/1/detail?page=1&sort=asc
   */
  toUrl(params?: RouteUrlParams): string {
    let url = (params?.partial ? this.path : this.fullPath) || "";

    if (url.substr(url.length - 1) === "/") {
      url = url.slice(0, -1);
    }

    if (!this.clickable) {
      url = "";
    }

    if (params?.pathParams) {
      const { pathParams } = params;

      Object.keys(pathParams).forEach((key) => {
        url = url.replace(`:${key.toLocaleLowerCase()}`, `${pathParams[key]}`);
      });
    }

    if (params?.queryParams) {
      url = `${url}?${queryString.stringify(params.queryParams)}`;
    }

    return url;
  }

  /**
   * @desc authorization check. returns true if user has access to this route
   * @example:
   * ===============
   * const { activeRoute } = useActiveRoute();
   * const access = activeRoute.getAuthorization();
   * if (access === false) {
   *   return <Forbidden />;
   * }
   */
  getAuthorization(fullAuthorization: true): RouteAuthorization | null | undefined;
  getAuthorization(fullAuthorization?: false): boolean | null | undefined;
  getAuthorization(fullAuthorization = false): RouteAuthorization | boolean | null | undefined {
    const currentUser = Cookies.get("CURRENT_USER");

    if (currentUser && typeof window !== "undefined") {
      const cookie = JSON.parse(currentUser) as CurrentUserCookie;

      if (cookie?.adminRights) {
        const activeRouteAuthorizationKey = this.authorizationKey ?? this.parent?.authorizationKey;
        const authorizations = cookie?.adminRights.find(
          (item) => item.name === activeRouteAuthorizationKey
        );
        const activeRoutePath = this.path.replace("/", "");

        if (fullAuthorization) {
          return authorizations;
        }

        if (activeRoutePath === "new" || activeRoutePath === "edit") {
          // write control
          return authorizations?.write;
        }

        // read control
        return authorizations?.read;
      }
    }

    return null;
  }
}

export default RouteBase;
